import { useCallback, useState } from 'react';
import { ChevronRight, Play } from 'lucide-react';
import { motion } from 'motion/react';
import type { FonotekaPack, FonotekaTrack } from '@/api/client';
import { useGlobalAudioPlayback } from '@/audio/GlobalAudioContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { FonotekaCollapsible } from './FonotekaCollapsible';
import { FonotekaTrackRow } from './FonotekaTrackRow';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

export type FonotekaPackBlockProps = {
  pack: FonotekaPack;
  expanded: boolean;
  onToggle: () => void;
  canWrite: boolean;
  canPickAuthor: boolean;
  onRenamePack: () => void;
  onDeletePack: () => void;
  onOpenUpload: () => void;
  onRenameTrack: (track: FonotekaTrack) => void;
  onDeleteTrack: (track: FonotekaTrack) => void;
  onOpenTagsCatalog: () => void;
  onToggleTrackTag: (trackId: string, tagId: string, on: boolean) => void;
  onSetTrackAuthor: (trackId: string, legalEntityId: string | null) => void;
  tagsSavingTrackId?: string | null;
  authorSavingTrackId?: string | null;
  uploadBusy?: boolean;
};

export function FonotekaPackBlock({
  pack,
  expanded,
  onToggle,
  canWrite,
  canPickAuthor,
  onRenamePack,
  onDeletePack,
  onOpenUpload,
  onRenameTrack,
  onDeleteTrack,
  onOpenTagsCatalog,
  onToggleTrackTag,
  onSetTrackAuthor,
  tagsSavingTrackId,
  authorSavingTrackId,
  uploadBusy = false,
}: FonotekaPackBlockProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { track: globalTrack } = useGlobalAudioPlayback();
  const showPackPlaying =
    globalTrack?.source === 'fonoteka' && globalTrack.packId === pack.id;

  const handleHeaderClick = useCallback(() => {
    if (menuOpen) return;
    onToggle();
  }, [menuOpen, onToggle]);

  const handleHeaderKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleHeaderClick();
      }
    },
    [handleHeaderClick],
  );

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        className="fonoteka-pack"
        onClick={handleHeaderClick}
        onKeyDown={handleHeaderKeyDown}
        aria-expanded={expanded}
      >
        <motion.span
          className="flex shrink-0 items-center text-muted-foreground"
          animate={{ rotate: expanded ? 90 : 0 }}
          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          aria-hidden
        >
          <ChevronRight className="size-3.5" strokeWidth={2} />
        </motion.span>
        <span className="flex min-w-0 flex-1 items-center gap-[0.45rem]">
          <span className="fonoteka-pack-name truncate">{pack.name}</span>
          <Badge variant="secondary">{pack.tracks.length}</Badge>
          {showPackPlaying ? (
            <span className="fonoteka-pack-playing" title="Воспроизводится из этого проекта" aria-hidden>
              <Play className="size-3.5" fill="currentColor" strokeWidth={0} />
            </span>
          ) : null}
        </span>
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="fonoteka-icon-btn"
              aria-label="Действия с проектом"
              onClick={(e) => e.stopPropagation()}
            >
              ⋮
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            {/* Название проекта трекера меняется вместе с проектом; переименовать можно только оставшийся без проекта */}
            {pack.project_id ? null : (
              <DropdownMenuItem disabled={!canWrite} onSelect={() => onRenamePack()}>
                Переименовать
              </DropdownMenuItem>
            )}
            <DropdownMenuItem disabled={!canWrite || uploadBusy} onSelect={() => onOpenUpload()}>
              Загрузить треки
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!canWrite}
              variant="destructive"
              onSelect={() => onDeletePack()}
            >
              Удалить
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <FonotekaCollapsible open={expanded}>
        {pack.tracks.length === 0 ? (
          <div className="fonoteka-pack-empty">
            <p>В проекте пока нет треков</p>
            {canWrite ? (
              <Button type="button" variant="outline" size="sm" disabled={uploadBusy} onClick={onOpenUpload}>
                Загрузить треки
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="fonoteka-track-list">
            {pack.tracks.map((track) => (
              <FonotekaTrackRow
                key={track.id}
                track={track}
                canWrite={canWrite}
                canPickAuthor={canPickAuthor}
                onRename={() => onRenameTrack(track)}
                onDelete={() => onDeleteTrack(track)}
                onOpenTagsCatalog={onOpenTagsCatalog}
                onToggleTag={(tagId, on) => onToggleTrackTag(track.id, tagId, on)}
                onSetAuthor={(legalEntityId) => onSetTrackAuthor(track.id, legalEntityId)}
                tagsSaving={tagsSavingTrackId === track.id}
                authorSaving={authorSavingTrackId === track.id}
              />
            ))}
          </div>
        )}
      </FonotekaCollapsible>
    </div>
  );
}
